/**
 * ActionButtons Component
 * Action buttons for result cards with icons and gradients
 */

import React from 'react';
import clsx from 'clsx';

export interface Action {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  variant?: 'outline' | 'ghost' | 'solid';
  disabled?: boolean;
}

interface ActionButtonsProps {
  actions: Action[];
  layout?: 'horizontal' | 'vertical';
}

export const ActionButtons: React.FC<ActionButtonsProps> = ({
  actions,
  layout = 'horizontal',
}) => {
  if (!actions || actions.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        'ai360-action-buttons',
        layout === 'vertical' && 'ai360-action-buttons--vertical'
      )}
    >
      {actions.map((action, index) => (
        <button
          key={`${action.label}-${index}`}
          type="button"
          onClick={action.onClick}
          disabled={action.disabled}
          className={clsx(
            'ai360-action-button',
            `ai360-action-button--${action.variant || 'outline'}`,
            action.disabled && 'ai360-action-button--disabled'
          )}
        >
          {action.icon && (
            <span className="ai360-action-button-icon">{action.icon}</span>
          )}
          <span className="ai360-action-button-label">{action.label}</span>
        </button>
      ))}
    </div>
  );
};

// Pre-built icon components for common actions
export const ActionIcons = {
  ViewMore: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 3v10m5-5H3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  Link: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M10 3h3v3m-3 7H7a4 4 0 01-4-4V6a4 4 0 014-4h3"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Map: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M1 5l5-2v10l-5 2V5zm5-2l4 2v10l-4-2V3zm4 2l5-2v10l-5 2V5z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  Save: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M8 12V4m0 8l-3-3m3 3l3-3M2 14h12"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
};
